import { useCallback, useMemo } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Card, CardContent, Chip, Grid, Stack, Typography, useTheme, Button } from '@mui/material'
import { DataGrid, GridColDef, GridToolbarContainer, useGridApiContext } from '@mui/x-data-grid'
import { useLocations } from '../../../hooks/locations'
import { useCurrentUser } from '../../../hooks/users'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { EmissionTier, MetricState, stateColorMapping, metricStateLabels } from '../common'
import { ExtremeWindScoreBadge } from '../common/extremeWind/utils'
import { useHistory } from 'react-router-dom'
import FilterAltIcon from '@mui/icons-material/FilterAlt'

function compactNumber(n: number) {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)
}

function fixed1(n: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n)
}

function StatusChip({ value }: { value?: MetricState | null }) {
  if (!value) return <span>—</span>
  const label = metricStateLabels[value] || value
  const color = stateColorMapping[value]
  return (
    <span style={{ background: color, color: 'white', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>
      {label}
    </span>
  )
}

function PropertyFilterToolbar() {
  const apiRef = useGridApiContext()
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', px: 0, py: 1 }}>
      <Typography variant="subtitle1">Property Breakdown</Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltIcon />}
          onClick={() => apiRef.current?.showFilterPanel?.()}>
          Filter
        </Button>
      </Stack>
    </GridToolbarContainer>
  )
}

type Row = {
  id: number
  name: string
  type: string

  statusScore?: MetricState
  statusLoss?: MetricState
  statusBI?: MetricState

  score?: number | null
  scoreYear?: number | null

  loss?: number | null
  lossYear?: number | null

  biDays?: number | null
  biYear?: number | null
}

export default function PortfolioExtremeWind({ emissionTier }: { emissionTier: EmissionTier }) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId })
  const { data: scoreByProperty } = usePortfolioClimateData('tc:score', { groupId: groupId ?? undefined })
  const { data: lossByProperty } = usePortfolioClimateData('tc:damage_loss', { groupId: groupId ?? undefined })
  const { data: biByProperty } = usePortfolioClimateData('tc:bi_days', { groupId: groupId ?? undefined })
  const { currencySymbol } = useCurrentUser()

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'tc', emissionTier)),
    [emissionTier, history],
  )

  const rows: Row[] = useMemo(() => {
    const base: Record<number, Row> = {}
    locations.forEach((p: any) => {
      base[p.id] = {
        id: p.id,
        name: p.name || `Property ${p.id}`,
        type: p.type.label,
      }
    })

    const tierKey = emissionTier as unknown as string // 'low' | 'moderate' | 'high'

    const pickPoint = (entry: any): { y: number | null; x: number | null } => {
      if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return { y: null, x: null }
      const node = entry.data?.data?.[tierKey]
      if (!node || typeof node !== 'object') return { y: null, x: null }
      const y = typeof node.y === 'number' ? node.y : null
      const x = typeof node.x === 'number' ? node.x : null
      return { y, x }
    }

    if (scoreByProperty) {
      Object.values(scoreByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || { id: entry.property_id, name: `Property ${entry.property_id}` }
        r.statusScore = entry.state?.value
        const { y, x } = pickPoint(entry)
        r.score = y
        r.scoreYear = x
        base[entry.property_id] = r
      })
    }

    if (lossByProperty) {
      Object.values(lossByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || { id: entry.property_id, name: `Property ${entry.property_id}` }
        r.statusLoss = entry.state?.value
        const { y, x } = pickPoint(entry)
        r.loss = y
        r.lossYear = x
        base[entry.property_id] = r
      })
    }

    if (biByProperty) {
      Object.values(biByProperty as Record<string, any>).forEach(entry => {
        const r = base[entry.property_id] || { id: entry.property_id, name: `Property ${entry.property_id}` }
        r.statusBI = entry.state?.value
        const { y, x } = pickPoint(entry)
        r.biDays = y
        r.biYear = x
        base[entry.property_id] = r
      })
    }

    return Object.values(base)
  }, [locations, emissionTier, scoreByProperty, lossByProperty, biByProperty])

  // KPIs (single projection point): avg score, total loss, avg BI days + coverage counts
  const kpis = useMemo(() => {
    const scoreVals = rows.map(r => r.score).filter((v): v is number => typeof v === 'number')
    const lossVals = rows.map(r => r.loss).filter((v): v is number => typeof v === 'number')
    const biVals = rows.map(r => r.biDays).filter((v): v is number => typeof v === 'number')

    const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
    const sum = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) : null)

    return {
      avgScore: mean(scoreVals),
      totalLoss: sum(lossVals),
      avgBiDays: mean(biVals),
      coverageScore: scoreVals.length,
      coverageLoss: lossVals.length,
      coverageBI: biVals.length,
    }
  }, [rows])

  const avgScoreRounded = kpis.avgScore != null ? Math.round(kpis.avgScore) : null

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      { field: 'name', headerName: 'Property', width: 240 },
      { field: 'type', headerName: 'Property Type', width: 140 },
      {
        field: 'score',
        headerName: 'Score',
        width: 160,
        valueGetter: params => (typeof params.row.score === 'number' ? params.row.score : null),
        renderCell: params => <ExtremeWindScoreBadge score={(params.value as number | null) ?? null} size="dense" />,
      },
      {
        field: 'loss',
        headerName: 'Damage Loss',
        width: 180,
        valueGetter: params => (typeof params.row.loss === 'number' ? params.row.loss : null),
        renderCell: params => (
          <span>
            {typeof params.value === 'number' ? `${currencySymbol}${compactNumber(params.value)} / year` : '—'}
          </span>
        ),
      },
      {
        field: 'biDays',
        headerName: 'Business Interruption',
        width: 220,
        valueGetter: params => (typeof params.row.biDays === 'number' ? params.row.biDays : null),
        renderCell: params => (
          <span>{typeof params.value === 'number' ? `${fixed1(params.value)} days/year` : '—'}</span>
        ),
      },
      {
        field: 'statusScore',
        headerName: 'Score Status',
        width: 140,
        sortable: false,
        valueGetter: p => p.row.statusScore ?? null,
        renderCell: p => <StatusChip value={p.value as MetricState | null} />,
      },
      {
        field: 'statusLoss',
        headerName: 'Damage Loss Status',
        width: 160,
        sortable: false,
        valueGetter: p => p.row.statusLoss ?? null,
        renderCell: p => <StatusChip value={p.value as MetricState | null} />,
      },
      {
        field: 'statusBI',
        headerName: 'BI Status',
        width: 120,
        sortable: false,
        valueGetter: p => p.row.statusBI ?? null,
        renderCell: p => <StatusChip value={p.value as MetricState | null} />,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <Button size="small" onClick={() => goToProperty(params.row.id)}>
            View property
          </Button>
        ),
      },
    ],
    [currencySymbol, goToProperty],
  )
  const PERIOD_LABEL = 'All decades (2020-2100)'
  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {/* KPI strip */}
        <Grid container spacing={2}>
          {/* Avg Score */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Average Risk Rating</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="body2">{PERIOD_LABEL}</Typography>
                    <ExtremeWindScoreBadge score={avgScoreRounded} size="dense" />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Damage Loss */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Total Damage Loss</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="body2">{PERIOD_LABEL}</Typography>
                    <Chip
                      label={kpis.totalLoss != null ? `${currencySymbol}${compactNumber(kpis.totalLoss)} / year` : '—'}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Average BI Days */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Business Interruption</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="body2">{PERIOD_LABEL}</Typography>
                    <Chip label={kpis.avgBiDays != null ? `${fixed1(kpis.avgBiDays)} days/year` : '—'} />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <div style={{ height: 520, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={r => r.id}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      statusScore: false,
                      statusLoss: false,
                      statusBI: false,
                    },
                  },
                  sorting: { sortModel: [{ field: 'score', sort: 'desc' }] },
                }}
                density="compact"
                onRowDoubleClick={params => goToProperty(params.row.id)}
                disableSelectionOnClick
                components={{ Toolbar: PropertyFilterToolbar }}
                sx={{
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover },
                }}
              />
            </div>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  )
}
